import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';

import { tap, catchError, lastValueFrom } from 'rxjs';

import { GlobalFiltersService } from '@app/root/app/shared';

import { PagedModel } from '@app/root/app/shared';
import {
	LeagueListViewModel,
	LeagueDashboardViewModel,
	LeagueDetailsViewModel,
	LeagueEditModel
} from '@app/shared';

import { handleHttpError } from '../../functions';
import { SystemConstants, AffiliationApplicationStatusCodesV2 } from '@app/constants';

import { Environment } from '@app/environment';
import { LeagueSearchItemViewModel } from '../models/league.models';

@Injectable({
	providedIn: 'root',
})
export class LeaguesService {
	private readonly baseUrl = `${Environment.apiBaseUrl}/leagues`;

	private httpClient: HttpClient = inject(HttpClient);
	private globalFiltersService: GlobalFiltersService = inject(GlobalFiltersService);

	searchLeagues(
		searchText: string | null,
		zoneID: number | null,
		regionID: number | null,
		affAppNotStarted: boolean | null,
		affAppDraft: boolean | null,
		affAppSubmitted: boolean | null,
		affAppRejected: boolean | null,
		affAppApproved: boolean | null,
		isLeagueAgreementSubmitted: boolean | null,
		isTournamentApplicationSubmitted: boolean | null,
		hasIncompleteTeams: boolean | null,
		hasMissingDocuments: boolean | null,
	) {
		return lastValueFrom(
			this.searchLeagues$(
				searchText,
				zoneID,
				regionID,
				affAppNotStarted,
				affAppDraft,
				affAppSubmitted,
				affAppRejected,
				affAppApproved,
				isLeagueAgreementSubmitted,
				isTournamentApplicationSubmitted,
				hasIncompleteTeams,
				hasMissingDocuments,
			)
		);
	}

	searchLeagues$(
		searchText: string | null,
		regionID: number | null,
		zoneID: number | null,
		affAppNotStarted: boolean | null,
		affAppDraft: boolean | null,
		affAppSubmitted: boolean | null,
		affAppRejected: boolean | null,
		affAppApproved: boolean | null,
		isLeagueAgreementSubmitted: boolean | null,
		isTournamentAppSubmitted: boolean | null,
		hasIncompleteTeams: boolean | null,
		hasMissingDocuments: boolean | null,
	) {
		const affAppStatusCodes: string[] = [];
		if (affAppNotStarted) {
			affAppStatusCodes.push(AffiliationApplicationStatusCodesV2.NotStarted);
		}
		if (affAppDraft) {
			affAppStatusCodes.push(AffiliationApplicationStatusCodesV2.Draft);
		}
		if (affAppSubmitted) {
			affAppStatusCodes.push(AffiliationApplicationStatusCodesV2.Submitted);
		}
		if (affAppRejected) {
			affAppStatusCodes.push(AffiliationApplicationStatusCodesV2.Rejected);
		}
		if (affAppApproved) {
			affAppStatusCodes.push(AffiliationApplicationStatusCodesV2.Approved);
		}

		const httpParams = new HttpParams().appendAll({
			yearId: this.globalFiltersService.getLeagueYearID() ?? '',
			searchText: searchText ?? '',
			regionId: regionID ?? '',
			zoneId: zoneID ?? '',
			affAppStatusCodes: affAppStatusCodes.join(','),
			isLeagueAgreementSubmitted: isLeagueAgreementSubmitted || '',
			isTournamentAppSubmitted: isTournamentAppSubmitted || '',
			hasIncompleteTeams: hasIncompleteTeams || '',
			hasMissingDocs: hasMissingDocuments || '',
		});

		return this.httpClient
			.get<LeagueSearchItemViewModel[]>(
				`${this.baseUrl}/search`,
				{ params: httpParams })
			.pipe(
				tap(async leagues => {
					leagues.forEach(league => this.computeLeagueSearchItemProperties(league));
				}),
				catchError(handleHttpError)
			);
	}

	getLeagues(
		categoryID: number | null,
		name: string | null,
		zoneID: number | null,
		regionID: number | null,
		page: number
	) {
		return lastValueFrom(
			this.getLeagues$(
				categoryID,
				name,
				zoneID,
				regionID,
				page
			)
		);
	}

	getLeagues$(
		categoryID: number | null,
		name: string | null,
		zoneID: number | null,
		regionID: number | null,
		page: number
	) {
		const httpParams = new HttpParams().appendAll({
			categoryId: categoryID ?? '',
			name: name ?? '',
			zoneId: zoneID ?? '',
			regionId: regionID ?? '',
			yearId: this.globalFiltersService.getLeagueYearID() ?? '',
			page: true,
			pageIndex: page,
			pageSize: 50
		});

		return this.httpClient
			.get<PagedModel<LeagueListViewModel>>(
				`${this.baseUrl}`,
				{ params: httpParams })
			.pipe(
				tap(async pagedLeagues => {
					pagedLeagues.pageData.forEach(league => this.computeLeagueProperties(league));
				}),
				catchError(handleHttpError)
			);
	}

	getLeagueDashboard(leagueID: number) {
		console.log(`LOADING LEAGUE DASHBOARD => ${this.globalFiltersService.getLeagueYearID() || 'NULL'}...`);

		const httpParams = new HttpParams().appendAll({
			regionId: '',
			yearId: this.globalFiltersService.getLeagueYearID() ?? ''
		});

		return lastValueFrom(
			this.httpClient
				.get<LeagueDashboardViewModel>(
					`${this.baseUrl}/${leagueID}/dashboard`,
					{ params: httpParams })
				.pipe(
					tap(async league => this.computeLeagueProperties(league)),
					catchError(handleHttpError)
				)
		);
	}

	getLeagueDetails(leagueID: number) {
		console.log(`LOADING LEAGUE DETAILS => ${this.globalFiltersService.getLeagueYearID() || 'NULL'}...`);

		const httpParams = new HttpParams().appendAll({
			regionId: '',
			yearId: this.globalFiltersService.getLeagueYearID() ?? ''
		});

		return lastValueFrom(
			this.httpClient
				.get<LeagueDetailsViewModel>(
					`${this.baseUrl}/${leagueID}`,
					{ params: httpParams })
				.pipe(catchError(handleHttpError))
		);
	}

	getLeagueAffiliationApplication(leagueID: number) {
		const httpParams = new HttpParams().appendAll({
			regionId: '',
			yearId: this.globalFiltersService.getLeagueYearID() ?? ''
		});

		return lastValueFrom(
			this.httpClient
				.get<LeagueDetailsViewModel>(
					`${this.baseUrl}/${leagueID}`,
					{ params: httpParams })
				.pipe(catchError(handleHttpError))
		);
	}

	saveLeague(league: LeagueEditModel) {
		if (!league.fullDirectorIds) {
			league.fullDirectorIds = [];
		}
		if (!league.guestDirectorIds) {
			league.guestDirectorIds = [];
		}

		const httpParams = new HttpParams().appendAll({
			regionId: '',
			yearId: this.globalFiltersService.getLeagueYearID() ?? ''
		});

		return lastValueFrom(
			this.httpClient
				.post<LeagueDetailsViewModel>(
					`${this.baseUrl}/${league.leagueId}`,
					league,
					{ params: httpParams })
				.pipe(catchError(handleHttpError))
		);
	}

	deleteLeague(id: number) {
		const httpParams = new HttpParams().appendAll({
			regionId: '',
			yearId: this.globalFiltersService.getLeagueYearID() ?? '',
		});

		return lastValueFrom(
			this.httpClient.delete(`${this.baseUrl}/${id}`, { params: httpParams })
				.pipe(catchError(handleHttpError))
		);
	}

	private computeLeagueSearchItemProperties(league: LeagueSearchItemViewModel) {
		const leagueFullAddressParts: string[] = [];
		if (league.address1) {
			leagueFullAddressParts.push(league.address1);
		}
		if (league.address2) {
			leagueFullAddressParts.push(league.address2);
		}
		if (league.city) {
			leagueFullAddressParts.push(league.city);
		}
		if (league.stateAbbreviation || league.postalCode) {
			const statePostalParts: string[] = [];
			if (league.stateAbbreviation) {
				statePostalParts.push(league.stateAbbreviation);
			}
			if (league.postalCode) {
				statePostalParts.push(league.postalCode);
			}
			leagueFullAddressParts.push(statePostalParts.join(' '));
		}
		if (league.countryCode || league.countryName) {
			leagueFullAddressParts.push(league.countryName || league.countryCode!);
		}
		league.leagueFullAddress = leagueFullAddressParts.length > 0 ? leagueFullAddressParts.join(', ') : null;

		league.hasTeamWarning = league.teamCount === 0;

		league.hasPlayerWarning = !league.arePlayersComplete;
		league.hasPlayerSuccess = league.arePlayersComplete;

		league.hasCoachWarning = !league.areCoachesComplete;
		league.hasCoachSuccess = league.areCoachesComplete;

		const badCount = league.requiredNotStartedDocumentCount + league.requiredRejectedDocumentCount;
		const goodCount = league.requiredInProgressDocumentCount + league.requiredApprovedDocumentCount;
		league.hasDocumentWarning = badCount > 0;
		league.hasDocumentSuccess = badCount === 0 && goodCount > 0;

		league.totalRequiredDocumentCount = league.requiredNotStartedDocumentCount +
			league.requiredInProgressDocumentCount +
			league.requiredRejectedDocumentCount +
			league.requiredApprovedDocumentCount;
		league.totalOptionalDocumentCount = league.optionalNotStartedDocumentCount +
			league.optionalInProgressDocumentCount +
			league.optionalRejectedDocumentCount +
			league.optionalApprovedDocumentCount;
	}

	private computeLeagueProperties(league: LeagueListViewModel | LeagueDashboardViewModel) {
		league.hasTeamWarning = league.tournamentTeamCount === 0;

		league.hasPlayerWarning = league.playerCount < (league.tournamentTeamCount * SystemConstants.TeamPlayersMin);
		league.hasPlayerSuccess = league.playerCount > 0 && league.playerCount >= (league.tournamentTeamCount * SystemConstants.TeamPlayersMin);

		league.hasCoachWarning = league.coachCount < (league.tournamentTeamCount * SystemConstants.TeamCoachesMin);
		league.hasCoachSuccess = league.coachCount > 0 && league.coachCount >= (league.tournamentTeamCount * SystemConstants.TeamCoachesMin);

		const badCount = league.notStartedCount + league.rejectedCount;
		const goodCount = league.inProgressCount + league.approvedCount;
		league.hasDocumentWarning = badCount > 0;
		league.hasDocumentSuccess = badCount === 0 && goodCount >= league.totalCount && league.totalCount > 0;
	}
}
